import React from "react";

import { faUpRightFromSquare } from "@fortawesome/pro-solid-svg-icons";
import { groupBy } from "lodash-es";

import { ExternalLink } from "src/base-components/ExternalLink";
import { Icon } from "src/base-components/Icon";
import { Tooltip } from "src/design-system/Tooltip";
import { ArchivedPill, DraftPill } from "src/flow/Pills";
import { UnpublishedTemplateDependency } from "src/flow/usePublishFlowVersionCallback";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import { getBaseUrl, getUrlToCaseTemplateVersionPage } from "src/router/urls";

type UnpublishedCaseTemplatesListProps = {
  unpublishedTemplates: UnpublishedTemplateDependency[];
};

export const UnpublishedCaseTemplatesList: React.FC<
  UnpublishedCaseTemplatesListProps
> = ({ unpublishedTemplates }) => {
  const { workspace, orgId } = useWorkspaceContext();

  const buildTemplatePageUrl = (templateId: string, versionId: string) =>
    getBaseUrl() +
    getUrlToCaseTemplateVersionPage(orgId, workspace.id, templateId, versionId);

  return (
    <>
      {Object.entries(groupBy(unpublishedTemplates, "templateId")).map(
        ([templateId, versions]) => {
          const template = versions[0].template;
          return (
            <ul key={templateId} className="pb-3">
              {[
                <li
                  key={templateId}
                  className="font-inter-semibold-13px text-gray-800"
                >
                  {template?.attributes.template_name || "Unknown Template"}
                </li>,
                versions.map((version) => (
                  <li
                    key={version.versionId}
                    className="flex items-center gap-x-2 border-b border-b-transparent py-3 [&:not(:last-child)]:border-gray-200"
                  >
                    <span className="mr-auto font-inter-normal-13px text-gray-500">
                      {version.version?.attributes.version_name ||
                        "Unknown Version (Archived)"}
                    </span>
                    {!version.version ||
                    version.version?.attributes.archived ? (
                      <ArchivedPill
                        dataLoc={`archived-pill-${version.versionId}`}
                        size="sm"
                        withIcon
                      />
                    ) : (
                      <DraftPill
                        dataLoc={`draft-pill-${version.versionId}`}
                        size="sm"
                        withIcon
                      />
                    )}
                    <Tooltip
                      placement="bottom-end"
                      title="Open in new tab"
                      asChild
                    >
                      <ExternalLink
                        href={buildTemplatePageUrl(
                          templateId,
                          version.versionId,
                        )}
                      >
                        <Icon
                          color="text-gray-500"
                          icon={faUpRightFromSquare}
                          size="xs"
                        />
                      </ExternalLink>
                    </Tooltip>
                  </li>
                )),
              ]}
            </ul>
          );
        },
      )}
    </>
  );
};
